import type { ReactNode } from 'react';
import { fitValueFontSize } from '../kpiFit.js';

export interface LuceKpiCardProps {
  label: string;
  value: ReactNode;
  unit?: string;
  /** Delta indicator — matches the dispatcher contract. Rendered as a
   *  triangle arrow (▲/▼/—) + the provided string in a tone-coloured
   *  mono caption. */
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  /** Secondary muted caption beneath the value, e.g. "vs. yesterday". */
  meta?: string;
  /** Stagger mount fade across a KPI row. */
  delayMs?: number;
  /** KpiCard `fit`: the value's length, for one-line sizing to the tile (../kpiFit.ts). */
  fitChars?: number;
}

/**
 * Luce-theme KPI tile — an instrument-cluster readout rather than a
 * clean SaaS card. Bezel border, mono numerals, amber unit suffix,
 * delta indicator with triangle arrow. Reads entirely from `--evi-*`
 * tokens so both Luce Dark (amber on black) and Luce Light (warmer
 * amber on cream) paint correctly without duplicated code.
 *
 * Animations: opacity mount fade on the outer card (staggerable via
 * `delayMs`); no looping animations. `prefers-reduced-motion: reduce`
 * collapses the fade to an instant snap.
 */
export function LuceKpiCard({
  label, value, unit, delta, meta, delayMs = 0, fitChars,
}: LuceKpiCardProps) {
  const fit = fitChars !== undefined;
  const arrow =
    !delta ? null
    : delta.direction === 'up' ? '▲'
    : delta.direction === 'down' ? '▼'
    : '—';
  const deltaColor =
    !delta ? 'var(--evi-text-mute)'
    : delta.direction === 'up' ? 'var(--evi-good)'
    : delta.direction === 'down' ? 'var(--evi-warn)'
    : 'var(--evi-text-mute)';

  return (
    <div
      style={{
        position: 'relative',
        background: 'var(--evi-surface)',
        border: '1.5px solid var(--evi-border-strong)',
        borderRadius: 12,
        padding: '16px 18px',
        boxShadow: 'var(--evi-card-glow)',
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
        overflow: 'hidden',
        opacity: 0,
        animation: `evi-fade-up 0.5s var(--evi-motion-fill) ${delayMs}ms forwards`,
        // Fill the parent cell + center the label / value / delta
        // stack inside it. The bezel-highlight `<div>` below uses
        // position:absolute so it ignores the flex layout; the
        // visible content stack centers cleanly within the cell.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        minWidth: 0,
        boxSizing: 'border-box',
        // fit: the value below sizes itself to this card's content box.
        ...(fit ? { containerType: 'inline-size' as const } : {}),
      }}
    >
      {/* Label — small-caps mono eyebrow */}
      <div
        style={{
          fontSize: 10,
          letterSpacing: 1.4,
          color: 'var(--evi-text-mute)',
          fontWeight: 600,
          textTransform: 'uppercase',
          // 10 -> 4: matches DefaultKpiCard's tighter stack so
          // the 2-row dashboard cell fits label + value + delta
          // without overflow: hidden clipping anything.
          marginBottom: 4,
          fontVariantNumeric: 'tabular-nums',
          // fit: a label too long for one line wraps at a word, two
          // even lines at a tighter line height.
          ...(fit ? { marginBottom: 2, lineHeight: 1.2, textWrap: 'balance' as const, maxWidth: '100%' } : {}),
        }}
      >
        {label}
      </div>

      {/* Value + unit */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: 6,
          marginBottom: delta || meta ? (fit ? 3 : 8) : 0,
          maxWidth: '100%',
        }}
      >
        <span
          style={{
            // 32 -> 26 so the Luce variant fits the 2-row
            // dashboard widget cell on the same footing as
            // DefaultKpiCard at 22. Luce's lighter weight (300)
            // and tighter -1px letter-spacing means 26 here
            // reads about the same metric height as Default's
            // 22 at weight 600.
            fontSize: 26,
            fontWeight: 300,
            color: 'var(--evi-text)',
            letterSpacing: '-1px',
            // 1 -> 1.1 so a wrapped value (e.g. unit pushed to a
            // second line on a narrow cell) doesn't crash into the
            // previous line.
            lineHeight: 1.1,
            fontVariantNumeric: 'tabular-nums',
            wordBreak: 'break-word',
            minWidth: 0,
            // fit: one line, never broken mid-number, sized to the tile.
            ...(fitChars !== undefined
              ? { fontSize: fitValueFontSize(fitChars, 26), whiteSpace: 'nowrap' as const, wordBreak: 'normal' as const }
              : {}),
          }}
        >
          {value}
        </span>
        {unit && (
          <span
            style={{
              fontSize: 13,
              color: 'var(--evi-charging)',
              fontWeight: 500,
              letterSpacing: 0.5,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {unit}
          </span>
        )}
      </div>

      {/* Delta + meta row. Direction arrow is rendered through the tone
          colour; the delta.value string is whatever the caller supplied
          ("+12%", "-3", "All online", …). */}
      {(delta || meta) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            fontSize: 11,
            fontVariantNumeric: 'tabular-nums',
            maxWidth: '100%',
            flexWrap: 'wrap',
            ...(fit ? { lineHeight: 1.2 } : {}),
          }}
        >
          {delta && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: deltaColor,
                fontWeight: 600,
              }}
            >
              <span aria-hidden>{arrow}</span>
              {delta.value}
            </span>
          )}
          {meta && (
            <span
              style={{
                color: 'var(--evi-text-mute)',
                letterSpacing: 0.3,
                // fit: wraps at a space only (a figure has no break point), two even lines.
                ...(fit ? { textWrap: 'balance' as const } : {}),
              }}
            >
              {meta}
            </span>
          )}
        </div>
      )}

      {/* Top-edge highlight — inset 1px that reads as a raised bezel
          on dark mode and disappears cleanly on light mode. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 12,
          boxShadow:
            'inset 0 1px 0 color-mix(in srgb, var(--evi-text) 6%, transparent)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
